import React, { useEffect, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Loader2, Trash2, Plus, Library, Sparkles, BookOpen, FileUp, FileText, AlertTriangle, X, Check } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { hasOwnerAccess, getDisplayName } from '../../lib/owner'
import { useReadingBook } from '../../hooks/useReadingBooks'
import { useReadingProgress } from '../../hooks/useReadingProgress'
import { useConfirm } from '../../contexts/ConfirmDialogContext'
import { putBookFile, deleteBookFile, hasBookFile, detectBookFormat } from '../../lib/readingFileStore'

function fmtSize(bytes) {
  if (!bytes) return ''
  const mb = bytes / (1024 * 1024)
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`
}

function fmtDate(iso, locale) {
  if (!iso) return ''
  try { return new Date(iso).toLocaleDateString(locale === 'ar' ? 'ar-EG' : 'en-CA', { month: 'short', day: 'numeric' }) }
  catch { return '' }
}

const STATUSES = ['wishlist', 'reading', 'paused', 'finished']

// ReadingBookPage — /reading/:bookId
//
// Detail view: status switcher, started/finished dates, optional cover,
// long-form notes textarea (own books only), and a highlights list
// with inline composer. Books owned by the partner are read-only —
// the page still renders, just without the edit affordances.
export function ReadingBookPage() {
  const { t, i18n } = useTranslation()
  const locale = i18n.language === 'ar' ? 'ar' : 'en'
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const { bookId } = useParams()
  const confirm = useConfirm()
  const {
    book, highlights, loading, isMine, updateBook, deleteBook, addHighlight, deleteHighlight,
  } = useReadingBook(bookId)
  const { position, lastReadAt } = useReadingProgress(bookId)

  const [notesDraft, setNotesDraft] = useState(null)
  const [highlightDraft, setHighlightDraft] = useState('')
  const [pageDraft, setPageDraft] = useState('')
  const [posting, setPosting] = useState(false)

  if (!authLoading && user && !hasOwnerAccess(user)) {
    return <Navigate to="/" replace />
  }
  if (authLoading || loading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin opacity-60" />
      </div>
    )
  }
  if (!book) {
    return (
      <div className="mx-auto max-w-md p-6 text-center">
        <p dir="auto" className="mb-4 text-base opacity-80">{t('reading.notFound')}</p>
        <button
          type="button"
          onClick={() => navigate('/reading')}
          className="rounded-full bg-rose-500 px-5 py-2 text-sm font-medium text-white"
        >
          {t('reading.back')}
        </button>
      </div>
    )
  }

  const handleStatus = async (next) => {
    if (next === book.status) return
    await updateBook({ status: next })
  }

  const saveNotes = async () => {
    if (notesDraft === null) return
    await updateBook({ notes: notesDraft })
    setNotesDraft(null)
  }

  const submitHighlight = async (e) => {
    e?.preventDefault?.()
    if (posting) return
    const text = highlightDraft.trim()
    if (!text) return
    const page = pageDraft.trim() ? parseInt(pageDraft.trim(), 10) : null
    setPosting(true)
    const result = await addHighlight(text, Number.isFinite(page) ? page : null)
    setPosting(false)
    if (!result.error) {
      setHighlightDraft('')
      setPageDraft('')
    }
  }

  const handleDeleteBook = async () => {
    const ok = await confirm({
      title: t('reading.confirmDelete'),
      confirmLabel: t('reading.delete'),
      destructive: true,
    })
    if (!ok) return
    const result = await deleteBook()
    if (!result.error) {
      await deleteBookFile(book.id) // drop the on-device blob too
      navigate('/reading', { replace: true })
    }
  }

  const ownerName = !isMine ? (getDisplayName(book.user_id, locale) || '') : ''
  const progressPct = Math.round(position || 0)

  return (
    <div className="mx-auto max-w-md space-y-5 p-4">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => navigate('/reading')}
          className="grid h-9 w-9 place-items-center rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800"
          aria-label={t('reading.back')}
        >
          <ArrowLeft className="h-5 w-5 rtl:-scale-x-100" />
        </button>
        {isMine && (
          <button
            type="button"
            onClick={handleDeleteBook}
            className="grid h-9 w-9 place-items-center rounded-full text-rose-500 hover:bg-rose-500/10"
            aria-label={t('reading.delete')}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="flex gap-3">
        <div className="aspect-[2/3] w-24 flex-shrink-0 overflow-hidden rounded-xl bg-neutral-200 dark:bg-neutral-800">
          {book.cover_url ? (
            <img src={book.cover_url} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="grid h-full place-items-center">
              <Library className="h-8 w-8 opacity-30" />
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <h1 dir="auto" className="text-xl font-semibold leading-tight">{book.title}</h1>
          {book.author && <p dir="auto" className="text-sm opacity-70">{book.author}</p>}
          {!isMine && ownerName && (
            <p dir="auto" className="text-[11px] uppercase tracking-wide opacity-50">
              {t('reading.fromShelf', { name: ownerName })}
            </p>
          )}
          {(book.started_at || book.finished_at) && (
            <div className="flex flex-wrap gap-2 pt-1 text-[11px] opacity-60">
              {book.started_at && (
                <span dir="auto">{t('reading.startedOn', { date: book.started_at })}</span>
              )}
              {book.finished_at && (
                <span dir="auto">{t('reading.finishedOn', { date: book.finished_at })}</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Real reading progress — the page's headline now, not the status
          chips. Driven by what the reader actually saved. */}
      {progressPct > 0 && (
        <div className="space-y-1">
          <div className="flex items-center justify-between text-[11px]">
            <span dir="auto" className="font-medium text-app-primary-500">{t('reading.progress.read', { n: progressPct })}</span>
            {lastReadAt && <span dir="auto" className="opacity-60">{t('reading.progress.lastRead', { date: fmtDate(lastReadAt, locale) })}</span>}
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-800">
            <div className="h-full rounded-full bg-rose-500 transition-[width]" style={{ width: `${Math.min(100, progressPct)}%` }} />
          </div>
        </div>
      )}

      {book.text_path && (
        <button
          type="button"
          onClick={() => navigate(`/reading/${book.id}/read`)}
          className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-rose-500 px-5 py-3 text-sm font-medium text-white"
        >
          <BookOpen className="h-4 w-4" />
          <span dir="auto">{progressPct > 0 ? t('reading.file.continue', { n: progressPct }) : t('reading.readBook')}</span>
        </button>
      )}

      <BookFile book={book} isMine={isMine} updateBook={updateBook} userId={user?.id} progressPct={progressPct} t={t} />

      {/* Near the end → a clear finish CTA (which kicks off the AI
          synthesis), instead of hunting for the status chip. */}
      {isMine && progressPct >= 98 && book.status !== 'finished' && (
        <button
          type="button"
          onClick={() => handleStatus('finished')}
          className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-500 px-5 py-3 text-sm font-semibold text-white"
        >
          <Check className="h-4 w-4" /> <span dir="auto">{t('reading.finishCta')}</span>
        </button>
      )}

      {(book.ai_synthesis || book.ai_synthesis_ar) && (
        <div className="space-y-2 rounded-2xl bg-rose-500/5 px-4 py-3">
          <div className="flex items-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5 text-rose-500" />
            <span dir="auto" className="text-[10px] font-semibold uppercase tracking-wide text-rose-500">
              {t('reading.aiSynthesisLabel')}
            </span>
          </div>
          <p dir="auto" className="text-sm leading-relaxed">
            {locale === 'ar'
              ? (book.ai_synthesis_ar || book.ai_synthesis)
              : (book.ai_synthesis || book.ai_synthesis_ar)}
          </p>
          {Array.isArray(book.ai_tags) && book.ai_tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {book.ai_tags.map((tag) => (
                <span key={tag} dir="auto" className="rounded-full bg-rose-500/15 px-2 py-0.5 text-[10px] uppercase tracking-wide text-rose-600 dark:text-rose-300">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
      {book.status === 'finished' && !book.ai_synthesis && !book.ai_synthesis_ar && (
        <div className="flex items-center gap-2 rounded-2xl bg-neutral-100 px-4 py-2 text-xs opacity-70 dark:bg-neutral-800">
          <Loader2 className="h-3 w-3 animate-spin" />
          <span dir="auto">{t('reading.aiSynthesisPending')}</span>
        </div>
      )}

      {/* Manual status override — secondary now that progress tells the
          real story. Collapsed under a quiet label. */}
      <div className="space-y-1.5">
        <p dir="auto" className="text-[11px] font-medium opacity-50">{t('reading.changeStatus')}</p>
        <div className="flex gap-1.5 overflow-x-auto">
          {STATUSES.map((s) => {
            const active = book.status === s
            return (
              <button
                key={s}
                type="button"
                onClick={() => handleStatus(s)}
                disabled={!isMine}
                className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors disabled:opacity-50 ${
                  active
                    ? 'bg-rose-500 text-white'
                    : 'bg-neutral-100 text-foreground dark:bg-neutral-800'
                }`}
              >
                <span dir="auto">{t(`reading.status.${s}`)}</span>
              </button>
            )
          })}
        </div>
      </div>

      <div>
        <p dir="auto" className="mb-1.5 text-xs font-medium opacity-70">{t('reading.notesLabel')}</p>
        <textarea
          dir="auto"
          value={notesDraft !== null ? notesDraft : (book.notes || '')}
          onChange={(e) => setNotesDraft(e.target.value)}
          onBlur={notesDraft !== null ? saveNotes : undefined}
          disabled={!isMine}
          rows={4}
          placeholder={isMine ? t('reading.notesPlaceholder') : ''}
          className="w-full rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm leading-relaxed outline-none focus:border-rose-400 disabled:opacity-70 dark:border-neutral-700 dark:bg-neutral-900"
        />
      </div>

      <div>
        <p dir="auto" className="mb-1.5 text-xs font-medium opacity-70">
          {t('reading.highlightsLabel')} <span className="opacity-50">({highlights.length})</span>
        </p>
        {isMine && (
          <form onSubmit={submitHighlight} className="space-y-1.5 rounded-xl border border-neutral-200 bg-neutral-50 p-2 dark:border-neutral-700 dark:bg-neutral-900">
            <textarea
              dir="auto"
              value={highlightDraft}
              onChange={(e) => setHighlightDraft(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder={t('reading.highlightPlaceholder')}
              className="w-full resize-none bg-transparent text-sm outline-none"
            />
            <div className="flex items-center gap-2">
              <input
                type="number"
                inputMode="numeric"
                value={pageDraft}
                onChange={(e) => setPageDraft(e.target.value)}
                placeholder={t('reading.pageNumber')}
                className="w-20 rounded-md border border-neutral-200 bg-transparent px-2 py-1 text-xs outline-none dark:border-neutral-700"
                min="1"
                max="9999"
              />
              <button
                type="submit"
                disabled={posting || !highlightDraft.trim()}
                className="ms-auto inline-flex items-center gap-1 rounded-full bg-rose-500 px-3 py-1 text-xs font-medium text-white disabled:opacity-50"
              >
                <Plus className="h-3 w-3" /> <span dir="auto">{t('reading.addHighlight')}</span>
              </button>
            </div>
          </form>
        )}

        <ul className="mt-2 space-y-2">
          {highlights.map((h) => (
            <li
              key={h.id}
              className="group rounded-xl border border-neutral-200 bg-card px-3 py-2 dark:border-neutral-800"
            >
              <p dir="auto" className="text-sm leading-snug">{h.body}</p>
              <div className="mt-1 flex items-center justify-between text-[10px] opacity-60">
                <span dir="auto">
                  {h.page_number ? t('reading.page', { n: h.page_number }) : ''}
                </span>
                {isMine && h.user_id === user?.id && (
                  <button
                    type="button"
                    onClick={() => deleteHighlight(h.id)}
                    className="opacity-0 transition-opacity group-hover:opacity-100"
                    aria-label={t('reading.deleteHighlight')}
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

// BookFile — the on-device book file (PDF/EPUB). The blob lives in
// IndexedDB only; book.file_format is the synced "a file exists" marker.
// Three states: no file (attach), file present on this device (open /
// replace / remove), file marked but absent here (it's on another
// device — offer to re-attach).
function BookFile({ book, isMine, updateBook, userId, progressPct = 0, t }) {
  const navigate = useNavigate()
  const [present, setPresent] = useState(null) // null = checking
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    let alive = true
    hasBookFile(book.id).then((p) => { if (alive) setPresent(p) })
    return () => { alive = false }
  }, [book.id])

  const attach = async (e) => {
    const f = e.target.files?.[0]
    e.target.value = ''
    if (!f) return
    const fmt = detectBookFormat(f)
    if (!fmt) { setError(t('reading.file.pickError')); return }
    setError(null)
    setBusy(true)
    await putBookFile(book.id, {
      blob: f, userId, fileName: f.name, mimeType: f.type, format: fmt, size: f.size,
    })
    await updateBook({ file_format: fmt, file_name: f.name, file_size: f.size })
    setPresent(true)
    setBusy(false)
  }

  const open = async () => {
    // Both PDF and EPUB open in the in-app reader. Confirm the bytes are
    // still on this device first so we don't route into an empty reader.
    if (!(await hasBookFile(book.id))) { setPresent(false); return }
    // Opening a wishlist book = you've started reading it.
    if (isMine && book.status === 'wishlist') updateBook({ status: 'reading' })
    navigate(`/reading/${book.id}/read`)
  }

  const remove = async () => {
    setBusy(true)
    await deleteBookFile(book.id)
    await updateBook({ file_format: null, file_name: null, file_size: null })
    setPresent(false)
    setBusy(false)
  }

  // No file at all → attach affordance (own books only).
  if (!book.file_format && !present) {
    if (!isMine) return null
    return (
      <div className="space-y-1">
        <label className="flex cursor-pointer items-center justify-center gap-2 rounded-2xl border border-dashed border-neutral-300 px-4 py-3 text-sm opacity-80 dark:border-neutral-700">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
          <span dir="auto">{t('reading.file.attach')}</span>
          <input type="file" accept=".pdf,.epub,application/pdf,application/epub+zip" onChange={attach} className="hidden" disabled={busy} />
        </label>
        {error && <p dir="auto" className="text-xs text-rose-500">{error}</p>}
        <p dir="auto" className="text-[11px] opacity-60">{t('reading.file.localOnlyHint')}</p>
      </div>
    )
  }

  // Marked as having a file but the bytes aren't on this device.
  if (book.file_format && present === false) {
    return (
      <div className="space-y-2 rounded-2xl bg-amber-500/10 px-4 py-3">
        <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span dir="auto" className="text-xs">{t('reading.file.missing')}</span>
        </div>
        {isMine && (
          <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl bg-rose-500 px-4 py-2 text-sm font-medium text-white">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
            <span dir="auto">{t('reading.file.reattach')}</span>
            <input type="file" accept=".pdf,.epub,application/pdf,application/epub+zip" onChange={attach} className="hidden" disabled={busy} />
          </label>
        )}
      </div>
    )
  }

  // File present on this device.
  if (present) {
    return (
      <div className="space-y-2 rounded-2xl border border-neutral-200 p-3 dark:border-neutral-800">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 flex-shrink-0 text-rose-500" />
          <span dir="auto" className="min-w-0 flex-1 truncate text-sm font-medium">{book.file_name || t(`reading.file.${book.file_format}`)}</span>
          <span className="rounded-md bg-rose-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase text-rose-500">{book.file_format}</span>
          {book.file_size ? <span dir="ltr" className="text-[11px] tabular-nums opacity-60">{fmtSize(book.file_size)}</span> : null}
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={open} className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-rose-500 px-4 py-2 text-sm font-medium text-white">
            <BookOpen className="h-4 w-4" /> <span dir="auto">{progressPct > 0 ? t('reading.file.continue', { n: progressPct }) : t('reading.file.start')}</span>
          </button>
          {isMine && (
            <>
              <label className="grid h-9 w-9 cursor-pointer place-items-center rounded-xl bg-neutral-100 dark:bg-neutral-800" aria-label={t('reading.file.replace')}>
                <FileUp className="h-4 w-4" />
                <input type="file" accept=".pdf,.epub,application/pdf,application/epub+zip" onChange={attach} className="hidden" disabled={busy} />
              </label>
              <button type="button" onClick={remove} disabled={busy} className="grid h-9 w-9 place-items-center rounded-xl text-rose-500 hover:bg-rose-500/10" aria-label={t('reading.file.remove')}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
              </button>
            </>
          )}
        </div>
        {error && <p dir="auto" className="text-xs text-rose-500">{error}</p>}
        <p dir="auto" className="text-[11px] opacity-60">{t('reading.file.localOnly')}</p>
      </div>
    )
  }

  return null
}
